import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';

import useDragScroll from '~/shared/utils/useDragScroll';

import {
  buildFallbackImageFromCDN,
  getProductImageAspectRatioString,
  getProductImageSizeString,
} from '../../utils/images';

const CarouselWrapper = styled.div`
  position: relative;
  aspect-ratio: ${(props) => props.$aspectRatio};
`;

const CarouselContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  display: flex;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x pan-y;
  scrollbar-width: none;
  user-select: none;

  @media (hover: hover) {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CarouselInner = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${keyframes`
        0% {
          transform: translateX(0);
          animation-timing-function: ease-out;
        }
        26.8% {
          transform: translateX(-75%);
          animation-timing-function: linear;
        }
        51.2% {
          transform: translateX(-75%);
          animation-timing-function: ease-in-out;
        }
        100% {
          transform: translateX(0);
        }
      `} 2.05s linear 1.2s;
    `}
`;

const CarouselSlide = styled.div`
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  position: relative;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 4px;
  left: 8px;
  right: 8px;
  height: 1px;
  z-index: 1;
  background-color: ${({ theme }) =>
    theme.colors.gridV2ProductCardImageScrollIndicator};
  border-radius: 8px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity
    ${({ $visible }) => ($visible ? '80ms ease-out' : '280ms ease-in')};
`;

const ScrollBar = styled.div`
  width: ${({ $count }) => 100 / $count}%;
  height: 100%;
  border-radius: 8px;
  background-color: ${({ theme }) =>
    theme.colors.gridV2ProductCardImageScrollBar};
  transform: translateX(${({ $activeIndex }) => $activeIndex * 100}%);
  transition: transform 0.2s ease-out;
`;

const ProductCardImageCarousel = ({
  imageUrls,
  isMobile,
  isFirstProductCard,
  swatchLabel,
  onFirstImageLoaded,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [carouselLoadedCount, setCarouselLoadedCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isIndicatorVisible, setIsIndicatorVisible] = useState(false);
  const carouselRef = useRef(null);
  const dragHandlers = useDragScroll(carouselRef);
  const slideRefs = useRef([]);
  const hideIndicatorTimerRef = useRef(null);

  // Scroll indicator should fade away when not in use
  const handleScrollActivity = useCallback(() => {
    clearTimeout(hideIndicatorTimerRef.current);
    setIsIndicatorVisible(true);
    hideIndicatorTimerRef.current = setTimeout(
      () => setIsIndicatorVisible(false),
      1500
    );
  }, []);

  useEffect(() => {
    hideIndicatorTimerRef.current = setTimeout(
      () => setIsIndicatorVisible(false),
      1500
    );
    return () => clearTimeout(hideIndicatorTimerRef.current);
  }, []);

  // reset carousel to beginning when swatch changes
  useEffect(() => {
    setActiveIndex(0);
    setCarouselLoadedCount(0);
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }
  }, [swatchLabel]);

  const allCarouselImagesLoaded =
    carouselLoadedCount > 0 && carouselLoadedCount === imageUrls.length;

  const handleCarouselImageLoaded = useCallback(
    (index) => {
      if (index === 0) {
        onFirstImageLoaded();
      }
      setCarouselLoadedCount((prev) => prev + 1);
    },
    [onFirstImageLoaded]
  );

  const handleImageError = (e) => {
    const fallbackImageUrl = buildFallbackImageFromCDN({
      requestedSize: getProductImageSizeString(isMobile),
    });

    if (e.target.src !== fallbackImageUrl) {
      e.target.onerror = null;
      e.target.src = fallbackImageUrl;
    }
  };

  useEffect(() => {
    if (!isMobile || !carouselRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.dataset.slideIndex);
            setActiveIndex(index);
          }
        });
      },
      {
        root: carouselRef.current,
        threshold: 0.5,
      }
    );

    const slides = slideRefs.current;
    slides.forEach((slide) => {
      if (slide) {
        observer.observe(slide);
      }
    });

    return () => {
      slides.forEach((slide) => {
        if (slide) {
          observer.unobserve(slide);
        }
      });
    };
  }, [isMobile, imageUrls.length, swatchLabel]);

  const setSlideRef = useCallback(
    (index) => (el) => {
      slideRefs.current[index] = el;
    },
    []
  );

  return (
    <CarouselWrapper $aspectRatio={getProductImageAspectRatioString(isMobile)}>
      <CarouselContainer
        ref={carouselRef}
        {...dragHandlers}
        onScroll={handleScrollActivity}
      >
        <CarouselInner
          $animate={
            isFirstProductCard && allCarouselImagesLoaded && !hasAnimated
          }
          onAnimationEnd={() => setHasAnimated(true)}
        >
          {imageUrls.map((url, index) => (
            <CarouselSlide
              key={url}
              ref={setSlideRef(index)}
              data-slide-index={index}
            >
              <CarouselImage
                src={url}
                alt={swatchLabel}
                title={swatchLabel}
                draggable={false}
                onError={handleImageError}
                onLoad={() => handleCarouselImageLoaded(index)}
                loading={index === 0 ? 'eager' : 'lazy'}
                data-autotag={`product-image-${index + 1}`}
              />
            </CarouselSlide>
          ))}
        </CarouselInner>
      </CarouselContainer>
      <ScrollIndicator $visible={isIndicatorVisible}>
        <ScrollBar $activeIndex={activeIndex} $count={imageUrls.length} />
      </ScrollIndicator>
    </CarouselWrapper>
  );
};

ProductCardImageCarousel.propTypes = {
  imageUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
  isFirstProductCard: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  onFirstImageLoaded: PropTypes.func.isRequired,
  swatchLabel: PropTypes.string,
};

export default ProductCardImageCarousel;
